/**
 * Ported from bare-minerals-theme, which re-renders each row through Foundation's
 * <dynamic-section>. Buxom has no such element, so rows refresh with a plain Section
 * Rendering API fetch instead, following component-variant-picker.js.
 */
class BundleProducts extends HHBaseElement {
  constructor() {
    super()

    this._handleVariantChange = this._handleVariantChange.bind(this)
    this._handleAddToCart = this._handleAddToCart.bind(this)

    this.bundleDiscountType = this.getAttribute('data-discount-type')
    this.bundleDiscountAmount = this.getAttribute('data-discount-amount')
    this.bundleVariantId = this.getAttribute('data-bundle-variant-id')
    this.currency = this.getAttribute('data-currency') || 'USD'
    this.locale = this.getAttribute('data-locale') || 'en-US'
    this.fixedDiscountPrice = this.getAttribute('data-fixed-discount-price')
  }

  connectedCallback() {
    super.connectedCallback?.()

    this.$addToCartButton = document.querySelector(
      this.getAttribute('data-add-to-cart-selector')
    )

    this._scopeAllRowDomIds()
    this._validateVariants()
    this.rows.forEach((row) => this._syncRowSelection(row))
    this._validateProducts()
    this._calculateBundlePrice()
    this._attachListeners()
  }

  disconnectedCallback() {
    this._removeListeners()
  }

  _attachListeners() {
    this.addEventListener('change', this._handleVariantChange, true)
    this.addEventListener('click', this._handleVariantChange, true)
    // Captured on document so this runs before any listener bound to the button itself.
    // product-variants also binds the main submit button, and on a multi-variant bundle
    // parent that would add the bare parent variant alongside the bundle.
    document.addEventListener('click', this._handleAddToCart, true)
  }

  _removeListeners() {
    this.removeEventListener('change', this._handleVariantChange, true)
    this.removeEventListener('click', this._handleVariantChange, true)
    document.removeEventListener('click', this._handleAddToCart, true)
  }

  get rows() {
    return [...this.querySelectorAll('.bundle-product-wrapper')]
  }

  _rowData(row) {
    try {
      return JSON.parse(
        row.querySelector('.bundle-product-data')?.textContent || '{}'
      )
    } catch (error) {
      console.warn('Bundle row data could not be parsed', error)
      return {}
    }
  }

  /**
   * Section HTML is identical for duplicate bundle rows (same product). Shade toggles /
   * radio names must be unique per row — prefix ids, label[for], aria-labelledby and names.
   */
  _scopeRowDomIds(row) {
    const instance = row.dataset.bundleInstanceId
    if (!instance) return

    const prefix = `bi-${String(instance).replace(/[^a-zA-Z0-9_-]/g, '_')}--`
    const root = row.querySelector('[section-id="product-bundle-card"]')
    if (!root || root.querySelector(`[id^="${prefix}"]`)) return

    // Scoping exists so duplicate rows do not share radio ids and names.
    // Product data scripts use classes and shared cache keys, not scoped ids.
    const scopable = (el) => el.tagName !== 'SCRIPT'

    const idMap = new Map()
    root.querySelectorAll('[id]').forEach((el) => {
      if (el.id && scopable(el) && !el.id.startsWith(prefix)) {
        idMap.set(el.id, prefix + el.id)
      }
    })

    root.querySelectorAll('[id]').forEach((el) => {
      const next = idMap.get(el.id)
      if (next && scopable(el)) el.id = next
    })

    root.querySelectorAll('label[for]').forEach((el) => {
      const target = el.getAttribute('for')
      if (target && idMap.has(target)) el.setAttribute('for', idMap.get(target))
    })

    root.querySelectorAll('[aria-labelledby]').forEach((el) => {
      const raw = el.getAttribute('aria-labelledby')
      if (!raw) return
      const next = raw
        .trim()
        .split(/\s+/)
        .map((id) => idMap.get(id) || id)
        .join(' ')
      if (next !== raw) el.setAttribute('aria-labelledby', next)
    })

    root.querySelectorAll('input[name], select[name]').forEach((el) => {
      const name = el.getAttribute('name')
      if (name && !name.startsWith(prefix)) el.setAttribute('name', prefix + name)
    })
  }

  _scopeAllRowDomIds() {
    this.rows.forEach((row) => this._scopeRowDomIds(row))
  }

  /** Drop shades the bundle was not configured to offer. */
  _validateVariants() {
    this.rows.forEach((row) => {
      const allowed = (row.getAttribute('data-bundle-variants') || '')
        .split(',')
        .filter(Boolean)
      if (!allowed.length) return

      row
        .querySelectorAll('input[data-variant-id], button[data-variant-id]')
        .forEach((input) => {
          if (!allowed.includes(input.dataset.variantId)) {
            input.closest('li, .js-input-wrapper')?.remove()
          }
        })

      row.querySelectorAll('.dropdown-menu').forEach((group) => {
        if (!group.querySelector('input[data-variant-id]')) group.remove()
      })
    })
  }

  /**
   * The card and the wrapper resolve their variant independently (Liquid vs the bundles
   * metafield), so they can disagree on first paint — and `data-selected-variant` is what
   * gets added to cart. The rendered card wins; the radio is checked to match.
   */
  _syncRowSelection(row) {
    const allowed = (row.getAttribute('data-bundle-variants') || '')
      .split(',')
      .filter(Boolean)
    const radios = [...row.querySelectorAll('input[data-variant-id]')]
    const buttons = [...row.querySelectorAll('button[data-variant-id]')]
    const checked = radios.find((r) => r.checked)
    const current = row.getAttribute('data-selected-variant')

    // The server's pick wins. product-variants checks the child's own
    // selected_or_first_available_variant, which need not be the shade this bundle allows
    // or the one the card rendered, and data-selected-variant is what goes to cart.
    // Buxom also renders sold-out swatches enabled, so DOM order proves nothing.
    let id = allowed.includes(current) ? current : null
    if (!id && checked && allowed.includes(checked.dataset.variantId)) {
      id = checked.dataset.variantId
    }
    if (!id) {
      id =
        radios.find((r) => !r.disabled && allowed.includes(r.dataset.variantId))
          ?.dataset.variantId || allowed[0]
    }
    if (!id) return

    if (id !== current) row.setAttribute('data-selected-variant', id)

    // The shade dropdown repeats a shade under each of its categories, one radio apiece.
    radios.forEach((r) => {
      if (r.dataset.variantId === id) r.checked = true
    })
    row.querySelector('variant-dropdown')?.setAttribute('variant', id)

    buttons.forEach((b) => {
      b.closest('.size-select-container')?.classList.toggle(
        'selected',
        b.dataset.variantId === id
      )
    })
  }

  _validateProducts() {
    this._updateButtonState(!!this.querySelector('[data-unavailable="true"]'))
  }

  _getBundleTotal() {
    return this.rows.reduce((total, row) => {
      const priceEl = row.querySelector('[data-selected-variant-price]')
      const price = priceEl
        ? parseFloat(priceEl.getAttribute('data-selected-variant-price'))
        : 0
      return total + price * (parseInt(row.getAttribute('data-qty'), 10) || 1)
    }, 0)
  }

  /**
   * Falls back to the undiscounted total rather than 0, so a bundle with a missing or
   * unrecognised discountType cannot advertise $0.00 or send `_bundle_price: "0"`. The
   * app applies the real discount at checkout either way.
   */
  _getBundlePrice() {
    const total = this._getBundleTotal()
    let price = total

    if (this.bundleDiscountType === 'fixed price' && this.fixedDiscountPrice) {
      price = parseFloat(this.fixedDiscountPrice) / 100
    }

    if (this.bundleDiscountType === 'dollar amount off total') {
      price = total - Math.floor(parseFloat(this.bundleDiscountAmount))
    }

    if (this.bundleDiscountType === 'percentage off total') {
      price = total - total * (parseFloat(this.bundleDiscountAmount) / 100)
    }

    if (!Number.isFinite(price) || price < 0) price = total

    return { total, price }
  }

  _formatPrice(price) {
    return new Intl.NumberFormat(this.locale, {
      style: 'currency',
      currency: this.currency,
    }).format(price)
  }

  _calculateBundlePrice() {
    const { total, price } = this._getBundlePrice()
    if (total <= 0) return price

    document.querySelectorAll('[data-bundle-price]').forEach((el) => {
      el.textContent = this._formatPrice(price) || '0.00'
    })

    // The parent variant carries no compare-at price, so the pre-discount total only
    // exists here — without this the PDP price shows no strikethrough.
    document.querySelectorAll('[data-bundle-original-price]').forEach((el) => {
      el.textContent = total > price ? this._formatPrice(total) : ''
      el.hidden = total <= price
    })

    return price
  }

  _handleVariantChange(event) {
    // Size options are buttons and never fire `change`, so clicks are handled too.
    const input = event.target.closest?.(
      'input[data-variant-id], button[data-variant-id]'
    )
    if (!input || !this.contains(input)) return

    const row = input.closest('.bundle-product-wrapper')
    if (!row) return

    const variantId = input.dataset.variantId
    if (!variantId || row.getAttribute('data-selected-variant') === variantId) {
      return
    }
    if (input.tagName === 'INPUT' && !input.checked) return

    row.setAttribute('data-selected-variant', variantId)
    this._renderRow(row, variantId)
  }

  /** Section Rendering API swap, mirroring component-variant-picker.js. */
  async _renderRow(row, variantId) {
    const url = row.dataset.cardUrl
    const destination = row.querySelector('.bundle-product-wrapper__card')
    if (!url || !destination) return

    try {
      const response = await fetch(
        `${url}?variant=${variantId}&section_id=product-bundle-card`
      )
      const source = new DOMParser()
        .parseFromString(await response.text(), 'text/html')
        .querySelector('[section-id="product-bundle-card"]')

      if (source) destination.replaceChildren(source)
    } catch (error) {
      console.warn('Bundle row could not be refreshed', error)
    } finally {
      this._scopeRowDomIds(row)
      this._validateVariants()
      this._syncRowSelection(row)
      this._validateProducts()
      this._calculateBundlePrice()
    }
  }

  _updateButtonState(disabled = false) {
    if (!this.$addToCartButton) return

    this.$addToCartButton.disabled = disabled
    this.$addToCartButton.classList.toggle('out-of-stock', disabled)
  }

  async _handleAddToCart(event) {
    if (!this.$addToCartButton || !event.target.closest?.('button, [type="submit"]')) {
      return
    }
    if (!this.$addToCartButton.contains(event.target)) return

    event.preventDefault()
    event.stopPropagation()

    // Stopping propagation also stops whatever used to put the button in its loading
    // state, and this handler awaits the cart request, so without a guard a second click
    // adds the bundle again before the first one lands.
    if (this.$addToCartButton.disabled || this.$adding) return
    this.$adding = true
    this.$addToCartButton.setAttribute('loading', true)

    const components = this.rows
      .map((row) => {
        const id = row.getAttribute('data-selected-variant')
        if (!id) return null

        const priceEl = row.querySelector('[data-selected-variant-price]')
        const price = priceEl
          ? parseFloat(priceEl.getAttribute('data-selected-variant-price'))
          : 0

        return {
          v: id,
          q: parseInt(row.getAttribute('data-qty'), 10) || 1,
          p: row.getAttribute('data-id') || '',
          op: price * 100,
          uid: this._rowData(row).variants?.[id]?.uid,
        }
      })
      .filter(Boolean)

    const { total, price } = this._getBundlePrice()

    try {
      await window.__CART__CONTROLLER__.add({
        payload: {
          id: this.bundleVariantId,
          quantity: 1,
          properties: {
            _is_bundle: 'true',
            _bundle_items: JSON.stringify({ data: components }),
            _bundle_price: `${price}`,
            _bundle_original_price: `${total}`,
          },
        },
      })
    } catch (error) {
      console.error('Error adding bundle to cart:', error)
    } finally {
      this.$adding = false
      this.$addToCartButton.removeAttribute('loading')
    }
  }
}

window.customElements.define('bundle-products', BundleProducts)
